import { Flex } from "antd";
import dayjs from "dayjs";
import { FC, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { getDebtListByPointIdThunk } from "tm-shared/api";
import { displayDateFormat } from "tm-shared/constants";
import { formatUAHStandard } from "tm-shared/utils";

import { useAsyncTaskQueue } from "@/hooks/core/useAsyncTaskQueue";
import { useAppDispatch, usePointDebtsSelector } from "@/hooks/redux";
import { BlockLoader } from "@/shared/ui/BlockLoader";
import { PrimaryText, SecondaryText } from "@/shared/ui/Typography";
import { UniversalTabs } from "@/shared/ui/UniversalTabs";

import { StyledDebtContainer } from "../Styled";

interface IDebtTab {
    hasDebt: boolean;
}

interface DebtItem {
    label: string;
    amount: number;
    date: string;
}

const DEFAULT_TAB = "tt";
const TABS = [{ label: "По магазину", value: DEFAULT_TAB }];

const NoDebtInfo: FC = () => (
    <StyledDebtContainer>
        <SecondaryText>Борг відсутній</SecondaryText>
    </StyledDebtContainer>
);

const DebtList: FC<{ debts: DebtItem[] }> = ({ debts }) => (
    <StyledDebtContainer>
        {debts.map(({ amount, label, date }) => (
            <Flex key={label} gap={8} align="center" justify="space-between">
                <SecondaryText>
                    {label} ({date})
                </SecondaryText>
                <PrimaryText>{formatUAHStandard(amount)} </PrimaryText>
            </Flex>
        ))}
    </StyledDebtContainer>
);

export const DebtTab: FC<IDebtTab> = ({ hasDebt }) => {
    const dispatch = useAppDispatch();
    const { pointId = "" } = useParams<{ pointId: string }>();

    const debts = usePointDebtsSelector(pointId);

    const normalizeDebts = useMemo(() => debts ?? {}, [debts]);

    const [activeTab, setActiveTab] = useState(TABS[0]?.value ?? DEFAULT_TAB);

    const totalDebt = useMemo(() => {
        if (!hasDebt) return 0;
        return Object.values(normalizeDebts)
            .filter((item) => item.pointId === pointId)
            .reduce((acc, item) => acc + item.debt, 0);
    }, [hasDebt, normalizeDebts, pointId]);

    const ttDebts = useMemo(
        () => Object.values(normalizeDebts),
        [normalizeDebts],
    );

    const { queue, progress } = useAsyncTaskQueue();

    useEffect(() => {
        if (hasDebt) {
            queue.add("", () =>
                dispatch(getDebtListByPointIdThunk({ pointId })),
            );

            queue.runParallel();
        }
    }, [dispatch, hasDebt, queue, pointId]);

    const renderDebtSummary = () => {
        if (!hasDebt) {
            return <NoDebtInfo />;
        }

        return (
            <StyledDebtContainer>
                <Flex gap={8} align="center" justify="space-between">
                    <SecondaryText>Залишок боргу</SecondaryText>
                    {totalDebt > 0 && (
                        <PrimaryText>
                            {formatUAHStandard(totalDebt)}
                        </PrimaryText>
                    )}
                </Flex>
            </StyledDebtContainer>
        );
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case "tt": {
                const ttData = ttDebts.map((item) => ({
                    label: item.consignmentId,
                    amount: item.debt ?? 0,
                    date: dayjs(item?.createdAt).format(displayDateFormat.date),
                }));

                return ttData.length > 0 ? (
                    <DebtList debts={ttData} />
                ) : (
                    <NoDebtInfo />
                );
            }

            default:
                return null;
        }
    };

    if (progress.isRunning) {
        return <BlockLoader />;
    }

    return (
        <Flex vertical gap={16}>
            {renderDebtSummary()}

            <UniversalTabs
                tabs={TABS}
                value={activeTab}
                maxWidth="100%"
                onChange={setActiveTab}
            />

            {renderTabContent()}
        </Flex>
    );
};
